<template>
    <div id="main">
        <div id="info">
            <div class="outer">
                <div class="inner">
                    <div id="infoTop">
                        <div id="infoLeft">
                            <h1>{{ opera.title }}</h1>
                            <p><i>{{ opera.placename }}, {{ opera.performance_year }} ({{ opera.country }})</i></p>
                            <span class="centeredTable">
                                <table>
                                    <tr>
                                        <td>Composer:</td>
                                        <td>{{ opera.composer }}</td>
                                    </tr>
                                    <tr>
                                        <td>Librettist:</td>
                                        <td>{{ opera.librettist }}</td>
                                    </tr>
                                </table>
                            </span>
                        </div>
                        <div id="infoRight" v-if="!!wikiImage.source">
                            <img :src="wikiImage.source" :alt="wikiImage.title" />
                        </div>
                    </div>
                    <div id="infoBelow">
                        <p v-if="!!wikiData">
                            {{ wikiData }}
                            <span v-if="!!wikiLink">
                                <br><br>
                                <a :href="wikiLink" target="_blank">Read more...</a>
                            </span>
                        </p>
                        <p v-else>
                            <br>
                            Couldn't load information from Wikipedia.
                            <span v-if="!!wikiLink">
                                <br><br>
                                <a :href="wikiLink" target="_blank">Search on wikipedia...</a>
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <h2>Find related operas</h2>
        <div id="related">
            <div class="outer">
                <div id="filterWrap">
                    <p>Filter based on...</p>
                    <ul id="filters" class="filters">
                        <li v-for="e in Object.keys(filterSettings)" :key="e"
                            @click="filterSettings[e].active = !filterSettings[e].active">
                            <input type="checkbox" :id="e" v-model="filterSettings[e].active" />
                            <label :for="e" @click.prevent>
                                Same {{ filterSettings[e].displayText }} ({{ e == 'country' ?
                                        getCountry(opera.placename) : opera[e]
                                }})
                            </label>
                        </li>
                        <li @click="selectAll" class="button"><b>(De)select all</b></li>
                    </ul>
                    <p><br>Sort by (ascending)...</p>
                    <ul id="sorting" class="filters">
                        <li v-for="e in Object.keys(sortingMethods)" :key="e" @click="sortingMethod = e">
                            <input type="radio" :id="e + 'radio'" v-model="sortingMethod" name="sort" :value="e" />
                            <label :for="e + 'radio'" @click.prevent>{{ sortingMethods[e] }}</label>
                        </li>
                    </ul>
                </div>
                <div id="resultsWrap">
                    <p>Found {{ filterResults.length }} other opera{{ filterResults.length == 1 ? '' : 's' }}</p>
                    <ul class="result">
                        <li v-for="elem in filterResults" :key="elem.id" class="result" @click="resetPage(elem.id)">
                            <b>{{ elem.title }}</b>, {{ elem.performance_year }}<br>
                            <i>{{ elem.placename }} ({{ getCountry(elem.placename)
                            }})</i><br><br>
                            <u>Composer</u><br>
                            {{ elem.composer }}<br>
                            <u>Librettist</u><br>
                            {{ elem.librettist }}<br>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    props: ['id', 'initialFilterSettings', 'initialSortingMethod'],

    emits: ['onSortingMethodChange'],

    data() {
        return {
            basicOpera: null,
            filterSettings: null,
            sortingMethods: {
                title: 'Title',
                composer: 'Composer',
                librettist: 'Librettist',
                performance_year: 'Year',
                placename: 'City',
                country: 'Country'
            },
            sortingMethod: 'title',
            wikiData: null,
            wikiLink: null,
            wikiImage: { source: null, title: null },
        };
    },

    created() {
        // the objects in filterSettings reference the same objects inside initialFilterSettings
        this.filterSettings = this.initialFilterSettings;
        this.sortingMethod = this.initialSortingMethod;

        this.basicOpera = this.getById(this.id);
        if (!this.basicOpera) {
            this.$router.push('/');
        }

        this.loadWikiData();
    },

    watch: {
        // filterSettings doesn't need a watcher, as the object references to the main app are kept when it is created

        sortingMethod(newVal) {
            this.$emit('onSortingMethodChange', newVal);
        },
    },

    computed: {
        ...mapGetters(['getAll', 'getById', 'getByOperaId', 'getByTitle', 'getByComposer', 'getByLibrettist', 'getByYear', 'getByCity', 'getByCountry', 'getCountry']),

        opera() {
            if (!this.basicOpera) return null;

            return { ...this.basicOpera, country: this.getCountry(this.basicOpera.placename) };
        },

        filterResults() {
            if (!this.opera) return [];

            let res = [];
            if (this.filterSettings.placename.active) res = this.getByCity(this.opera.placename);
            else if (this.filterSettings.country.active) res = this.getByCountry(this.getCountry(this.opera.placename));
            else {
                for (const [key, value] of Object.entries(this.getAll)) {
                    // add the id to the returned object and make it an array
                    res.push({ id: key, ...value });
                }
            }

            res = res.filter(e => {
                // true either because the filter is inactive or it matches
                const notActive = this.opera.id !== e.id;
                const titleMatch = !this.filterSettings.title.active || this.opera.title == e.title;
                const composerMatch = !this.filterSettings.composer.active || this.opera.composer == e.composer;
                const librettistMatch = !this.filterSettings.librettist.active || this.opera.librettist == e.librettist;
                const yearMatch = !this.filterSettings.performance_year.active || this.opera.performance_year == e.performance_year;

                return notActive && titleMatch && composerMatch && librettistMatch && yearMatch;
            })

            // we sort les stuffs
            res = res.sort((a, b) => {
                let aVal = a[this.sortingMethod];
                let bVal = b[this.sortingMethod];
                if (this.sortingMethod == 'country') {
                    aVal = this.$store.getters.getCountry(a.placename);
                    bVal = this.$store.getters.getCountry(b.placename);
                }

                if (aVal < bVal) {
                    return -1;
                }
                if (aVal > bVal) {
                    return 1;
                }
                return 0;
            });

            return res;
        },
    },

    methods: {
        async loadWikiData() {
            const query = encodeURI(this.basicOpera.title);
            this.wikiLink = `https://en.wikipedia.org/w/index.php?search=${query}`
            const dataURL = `https://en.wikipedia.org/w/api.php?action=query&prop=extracts&exintro&explaintext&redirects=1&titles=${query}&origin=*&format=json`;
            const imgURL = `https://en.wikipedia.org/w/api.php?action=query&prop=pageimages&exintro&explaintext&redirects=1&titles=${query}&origin=*&format=json&pithumbsize=300`;

            const res = await fetch(dataURL);
            const resData = await res.json();
            console.log(resData);

            for (const key in resData.query.pages) {
                const element = resData.query.pages[key];
                this.wikiData = element.extract;
            }

            const res2 = await fetch(imgURL);
            const resData2 = await res2.json();
            console.log(resData2);

            for (const key in resData2.query.pages) {
                const element = resData2.query.pages[key];
                this.wikiImage = element.thumbnail ? element.thumbnail : { source: null, title: null };
            }
        },

        selectAll() {
            const anySelected = this.filterSettings.title.active && this.filterSettings.composer.active && this.filterSettings.librettist.active && this.filterSettings.performance_year.active && this.filterSettings.placename.active && this.filterSettings.country.active;
            const newValue = !anySelected;

            this.filterSettings.title.active = newValue;
            this.filterSettings.composer.active = newValue;
            this.filterSettings.librettist.active = newValue;
            this.filterSettings.performance_year.active = newValue;
            this.filterSettings.placename.active = newValue;
            this.filterSettings.country.active = newValue;
        },

        resetPage(id = this.opera.id) {
            this.basicOpera = null;
            this.basicOpera = this.getById(this.id);

            this.wikiData = null;
            this.wikiLink = null;
            this.wikiImage = { source: null, title: null },

                this.$router.push(id);
            this.loadWikiData();
        }
    },
}
</script>

<style scoped>
.outer {
    margin: 3rem;
    border: 3px solid lightblue;
    border-radius: 2rem;
}

.inner {
    padding: 3rem;
}

div#info,
div#related {
    text-align: center;
    width: 100%;
}

div#infoTop {
    display: flex;
    width: 85%;
    margin-left: auto;
    margin-right: auto;
    justify-content: space-around;
    align-items: center;
}

#infoBelow {
    width: 80%;
    margin-left: auto;
    margin-right: auto;
}

div#infoLeft {
    flex-grow: 2;
    font-size: large;
}

div#infoRight {
    flex-grow: 3;
}

#infoLeft h1 {
    margin-top: 0rem;
    margin-bottom: 0rem;
}

#infoLeft h1+p {
    margin-top: 0.5rem;
}

span.centeredTable {
    display: table;
    margin: 0 auto;
    text-align: left;
    padding-bottom: 1rem;
}

td:nth-child(1) {
    padding-right: 10px;
}

#infoBelow>p {
    margin-bottom: 0;
}

#main>h2 {
    margin-top: 3rem;
}

#related .outer {
    display: flex;
    justify-content: center;
    margin-top: 0;
}

div#filterWrap {
    flex-grow: 0;
    justify-self: flex-start;
    margin: 0 auto;
    margin-right: 1rem;
    min-width: 20%;
    border: 0;
    border-right: lightblue 2px solid;
    background-color: #add8e680;
    border-top-left-radius: 1.8rem;
    border-bottom-left-radius: 1.8rem;
    text-align: left;
}

div#resultsWrap {
    flex-grow: 1;
    margin: 2rem;
    margin-left: 1rem;
}

#filterWrap>p {
    margin-bottom: 0;
    font-weight: bold;
    margin-left: 1.5rem;
}

ul.filters {
    margin: 0 auto;
    list-style-type: none;
    vertical-align: middle;
    padding: 0.5rem;
    padding-top: 0;
}

ul.filters li {
    padding: 0.25rem;
    font-size: 11pt;
    border: transparent 1px solid;
    border-radius: 1rem;
    display: flex;
    align-items: center;
}

ul.filters li.button {
    margin-top: 0.1rem;
    background-color: #add8e6;
    border: rgb(150, 190, 226) 1px solid;
    padding: 0.5rem;
    padding-left: 0.8rem;
}

ul.filters li:hover {
    background-color: #85c5b0;
    border: #2b8d45 1px solid;
    cursor: pointer !important;
}

ul.filters li.button:hover {
    background-color: #85c5b0;
    border: #2b8d45 1px solid;
}

label:hover,
input:hover {
    cursor: pointer !important;
}

.filters label {
    display: inline-block;
    width: 80%;
    margin-left: 0.5rem;
    margin-right: auto;
}

#resultsWrap>p {
    margin-top: 0;
}

ul.result {
    padding: 0;
    margin-bottom: 0;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 600px;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
}

li.result {
    display: inline-block;
    width: 20%;
    padding: 0.7rem;
    margin: 0.8rem auto;
    border: #999 1px solid;
    border-radius: 1rem;
}

li.result:hover {
    box-shadow: 0 0 5px cyan;
    cursor: pointer;
}

/* Designing for scroll-bar */
::-webkit-scrollbar {
    width: 10px;
    background-color: transparent;
}

/* Track */
::-webkit-scrollbar-track {
    background: gainsboro;
    border-radius: 5px;
}

/* Handle */
::-webkit-scrollbar-thumb {
    background: black;
    border-radius: 5px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* separate styling for mobile devices */
@media all and (orientation: portrait) {
    .outer {
        margin: 1rem;
    }

    .inner {
        padding: 1rem;
    }

    div#infoTop,
    div#infoBelow {
        display: block;
        width: 95%;
    }

    #related .outer {
        display: block;
    }

    div#filterWrap {
        border-top-right-radius: 1.8rem;
        border-bottom-left-radius: unset;
        border-right: none;
        border-bottom: lightblue 2px solid;
        margin: 0;
        padding: 1rem;
        text-align: center;
    }

    #filterWrap>p {
        margin: 0;
        margin-bottom: 0.5rem;
    }

    ul.filters li {
        display: inline-flex;
        margin-left: 0.5rem;
        margin-right: 0.5rem;
        text-align: left;
    }

    .filters label {
        margin-left: 0.25rem;
        width: unset;
    }

    ul.filters li.button {
        margin: 0;
        margin-left: 0.5rem;
        margin-right: 0.5rem;
        padding: 0.25rem;
        padding-left: 0.5rem;
        padding-right: 1rem;
    }
}
</style>
